import { useEffect } from 'react';

/**
 * Ask the browser to confirm before the page goes away.
 *
 * For work that lives in the tab and cannot be picked up again anywhere else. A
 * bulk run is exactly that: it is a loop in this page, so closing it at sixty of
 * a hundred leaves forty conversations untouched with nothing server-side that
 * knows how far it got. The employer cannot be told that afterwards, so they are
 * asked before.
 *
 * The wording is the browser's own — every engine ignores whatever string a page
 * supplies, and has for years. `preventDefault` is what actually arms the
 * prompt; `returnValue` is set alongside it for the older engines that still
 * read it.
 *
 * This is a warning, not a guarantee: a crash, a closed laptop or a killed tab
 * takes the run with it regardless. The fix for that is moving the fan-out to
 * the server, not a louder prompt.
 */
export function useUnloadGuard(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    const guard = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', guard);
    return () => window.removeEventListener('beforeunload', guard);
  }, [active]);
}
